<script setup>
import axios from 'axios';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { shortDate } from '../../../helpers/usDate';
import Loading from '../../Loading.vue';
import XlButton from '../../xl/XlButton.vue';
import XlCard from '../../xl/XlCard.vue';
import XlField from '../../xl/XlField.vue';
import XlEmptyState from '../../xl/XlEmptyState.vue';
import XlModal from '../../xl/XlModal.vue';
import XlPagination from '../../xl/XlPagination.vue';
import XlSearchField from '../../xl/XlSearchField.vue';
import XlDropdown from '../../xl/XlDropdown.vue';
import XlTable from '../../xl/XlTable.vue';
import IconDoc from '../../icons/IconDoc.vue';
import IconImage from '../../icons/IconImage.vue';
import IconPdf from '../../icons/IconPdf.vue';
import IconPpt from '../../icons/IconPpt.vue';
import IconTxt from '../../icons/IconTxt.vue';
import ContentRowMenu from '../../content/ContentRowMenu.vue';
import ManageAccessModal from '../../resource-access/ManageAccessModal.vue';
import AddedBy from '../../resource-access/AddedBy.vue';
import ResourceAccessTag from '../../resource-access/ResourceAccessTag.vue';
import { bookFor, bookOfScope } from '../../../composables/useResourceAccess';
import UploadFilesModal from './UploadFilesModal.vue';
import useHubMembers from '../../../composables/useHubMembers';
import useResourceDownload from '../../../composables/useResourceDownload';
import { GB_CONVERSION } from '../../../helpers/constants';
import { downloadErrorMessage, downloadFailureIsStale, getFileErrorMessage, getFileType } from '../../../helpers/helper';
import { countOrNull } from '../../../helpers/contentCounts';
import { fitColumns } from '../../../helpers/fitColumns';
import { hubEmptyState } from '../../../helpers/hubEmptyStates';
import { CAPABILITIES, can, hubReader, isBroker } from '../../../helpers/roles';
import { useAuthStore } from '../../../stores/auth';

/**
 * The one management table for hub files. Resources & Content mounts it
 * directly, and so does the hub drawer when it expands to full width.
 * The whole (≤100) set is fetched once and searched, filtered, sorted and
 * paged here, so every control acts on all rows.
 */
const props = defineProps({
  actionsDisabled: { type: Boolean, default: false },
  otherShared: { type: Number, default: null },
});
const emit = defineEmits(['count']);

// Values are strings, because a native select hands back strings; `perPage` is
// the number. The label says "per page" rather than "shown": a hub with three
// files still has a page size of ten, and "10 shown" there would be a lie.
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100].map((n) => ({ value: String(n), label: `${n} per page` }));
const TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'pdf', label: 'PDF' },
  { value: 'ppt', label: 'Slides' },
  { value: 'images', label: 'Image' },
  { value: 'doc', label: 'Word' },
  { value: 'txt', label: 'Text' },
];
const TYPE_LABEL = { pdf: 'PDF', ppt: 'Slides', images: 'Image', doc: 'Word', txt: 'Text' };
const ICONS = { pdf: IconPdf, ppt: IconPpt, images: IconImage, doc: IconDoc, txt: IconTxt };
const ACCEPT = 'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/jpeg,image/png,image/gif,text/plain,application/pdf';

const api = () => window.store.event_backend_api;
const notify = (message, error = false) => window.loadNotification?.({ message, error });

const authStore = useAuthStore();
const canManage = computed(() => can(authStore.userRole, CAPABILITIES.CONTRIBUTE_HUB_CONTENT, authStore.capabilities));
// A broker sees every file that reaches their book but changes only their own.
const mine = (row) => !isBroker(authStore.userData) || row.created_by === authStore.userData?.subdomain_user_id;
// Ticks and the bulk bar exist only for someone who can act on the selection.
const canSelect = computed(() => canManage.value && !props.actionsDisabled);
const { members, loadHubMembers } = useHubMembers();
const { download } = useResourceDownload();

const rows = ref([]);
const loading = ref(true);
const failed = ref(false);
const busy = ref(false);
const totalSizeBytes = ref(0);
const capacityGb = ref(null);

const search = ref('');
const typeFilter = ref('all');
const pageSize = ref('10');
const sortKey = ref(null);
const sortDir = ref(1);
const selected = ref([]);
const page = ref(1);

const uploading = ref(false);
const accessTarget = ref(null);
const bulkIds = ref(null);
const renaming = ref(null);
const renameValue = ref('');
const renameError = ref('');
const deleting = ref([]);
const menuRowId = ref(null);
const replacingId = ref(null);

const addEl = ref(null);
const tableEl = ref(null);
const colWidths = ref([]);
const replaceInput = ref(null);

// ---- data ----

const load = async () => {
  loading.value = true;
  failed.value = false;
  try {
    const { data } = await axios.get(`${api()}/resources`, { params: { page: 1, per_page: 100, with_counts: 1 } });
    rows.value = data?.data ?? [];
    totalSizeBytes.value = data?.meta?.total_size ?? data?.total_size ?? 0;
  } catch (error) {
    console.error(error);
    failed.value = true;
    notify('Could not load the files.', true);
  } finally {
    loading.value = false;
  }
};

// The storage quota lives on the plan, not the listing, so its failure must
// not take the list down with it.
const loadCapacity = async () => {
  try {
    const { data } = await axios.get(`${api()}/subdomain-data`);
    const storage = (data?.data?.features ?? []).find((f) => f.group_name === 'storage');
    capacityGb.value = storage ? Number.parseInt(storage.value, 10) : null;
  } catch (error) {
    console.error(error);
  }
};

onMounted(async () => {
  await load();
  await Promise.all([loadCapacity(), loadHubMembers()]);
});

watch([() => rows.value.length, loading, failed], ([n, isLoading, hasFailed]) => emit('count', countOrNull(n, isLoading, hasFailed)), { immediate: true });

const waitingOnOthers = computed(() => !rows.value.length && !failed.value && props.otherShared === null);
const firstRun = computed(() => {
  if (loading.value || failed.value || rows.value.length || props.otherShared === null) return null;
  return hubEmptyState('content', {
    reader: hubReader(authStore),
    role: authStore.userRole,
    capabilities: authStore.capabilities,
    empty: props.otherShared === 0,
  });
});
const onFirstRunAction = (id) => {
  if (id === 'add-file') uploading.value = true;
};
watch([search, typeFilter, pageSize], () => { page.value = 1; });
// `canSelect` is the capability AND the block switch, so watching it covers
// both. Watching only `actionsDisabled` left a selection standing if the role
// changed under a mounted table.
watch(canSelect, (ok) => { if (!ok) selected.value = []; });

// ---- view ----

const formatBytes = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
const addedText = (iso) => shortDate(iso, { time: true });
const kindOf = (row) => getFileType(row.type);
const people = (n) => `${new Intl.NumberFormat().format(n)} ${n === 1 ? 'person' : 'people'}`;

// A long upload name pushes every column after it off the row, so it is cut
// at a length that still tells you which file this is; the whole name is on
// the row's title for anyone who needs the rest.
const NAME_MAX = 50;
const displayName = (name) => ((name || '').length > NAME_MAX ? `${name.slice(0, NAME_MAX).trimEnd()}…` : (name || ''));

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  let list = rows.value;
  if (q) list = list.filter((r) => (r.name || '').toLowerCase().includes(q));
  if (typeFilter.value !== 'all') list = list.filter((r) => kindOf(r) === typeFilter.value);
  if (!sortKey.value) return list;
  const cmp = {
    name: (a, b) => (a.name || '').localeCompare(b.name || ''),
    type: (a, b) => kindOf(a).localeCompare(kindOf(b)),
    added: (a, b) => Date.parse(a.created_at || 0) - Date.parse(b.created_at || 0),
  }[sortKey.value];
  return [...list].sort((a, b) => sortDir.value * cmp(a, b));
});

const perPage = computed(() => Number(pageSize.value));
const lastPage = computed(() => Math.max(1, Math.ceil(filtered.value.length / perPage.value)));
const currentPage = computed(() => Math.min(page.value, lastPage.value));
const paged = computed(() => filtered.value.slice((currentPage.value - 1) * perPage.value, currentPage.value * perPage.value));
const from = computed(() => (filtered.value.length ? (currentPage.value - 1) * perPage.value + 1 : 0));
const to = computed(() => Math.min(currentPage.value * perPage.value, filtered.value.length));

// asc → desc → off
const toggleSort = (key) => {
  if (sortKey.value !== key) {
    sortKey.value = key;
    sortDir.value = 1;
  } else if (sortDir.value === 1) {
    sortDir.value = -1;
  } else {
    sortKey.value = null;
    sortDir.value = 1;
  }
};
// Only the column doing the sorting wears a mark; the rest stay quiet.
const sortMark = (key) => (sortKey.value === key ? (sortDir.value === 1 ? '↑' : '↓') : '');

// ---- column widths ----

// The header swap would otherwise move the columns: the table is auto-layout,
// which measures each column from every cell in it, headers included, so
// collapsing seven headers into one `colspan` remeasures the lot from the body.
//
// Freezing the widths stops that. They are measured off the real layout rather
// than declared, because the table has to look exactly as it did — a set of
// invented widths fixes the shift and changes the spacing, which is a different
// bug wearing the fix's clothes.
const measureCols = async () => {
  // Not while a selection is up: the header is one colspan cell at that moment,
  // so measuring would replace eight columns with two and collapse the table.
  if (selected.value.length) return;
  // Back to auto first, or this measures the widths it froze last time.
  colWidths.value = [];
  await nextTick();
  const head = tableEl.value?.$el?.querySelector('thead tr');
  if (!head) return;
  const widths = [...head.children].map((th) => th.offsetWidth);
  if (!widths.every((w) => w > 0)) return;
  const fitted = fitColumns(widths, tableEl.value.$el.clientWidth, canSelect.value);
  colWidths.value = fitted.map((w) => `${w}px`);
};

// The hub sidebar animates to its new width after a window resize, so only the
// table's own box knows when it has settled.
let boxWidth = null;
const onBoxResize = ([entry]) => {
  if (entry.target.clientWidth === boxWidth) return;
  boxWidth = entry.target.clientWidth;
  measureCols();
};
const boxObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onBoxResize);
watch(tableEl, (table) => {
  boxObserver?.disconnect();
  if (table) boxObserver?.observe(table.$el);
});
onBeforeUnmount(() => boxObserver?.disconnect());
// Anything that changes what a column has to hold changes its natural width.
// measureCols skips while a selection is up, so it runs again once that clears.
watch(() => [loading.value, canSelect.value, paged.value.length, perPage.value, selected.value.length === 0], measureCols);

// ---- selection ----

const pageIds = computed(() => paged.value.filter(mine).map((r) => r.id));
const allOnPage = computed(() => pageIds.value.length > 0 && pageIds.value.every((id) => selected.value.includes(id)));
const toggleAll = () => {
  selected.value = allOnPage.value
    ? selected.value.filter((id) => !pageIds.value.includes(id))
    : [...new Set([...selected.value, ...pageIds.value])];
};
const toggleRow = (id) => {
  const i = selected.value.indexOf(id);
  if (i === -1) selected.value.push(id);
  else selected.value.splice(i, 1);
};
const selectedRows = computed(() => rows.value.filter((r) => selected.value.includes(r.id)));

// Ticking the header box takes this page. Everything the filters match is a
// second, explicit step — "select all" across rows you cannot see should never
// be the thing that happens by accident.
// Counting was wrong across a filter change: a selection made under one filter
// can be larger than what the current one matches, which hid the offer while
// matching rows were still unticked. Ask the rows instead.
const canSelectAll = computed(() => allOnPage.value
  && filtered.value.length > paged.value.length
  && filtered.value.some((r) => mine(r) && !selected.value.includes(r.id)));
const selectAllMatching = () => { selected.value = filtered.value.filter(mine).map((r) => r.id); };

// ---- audience ----

const openAccess = (row) => {
  if (!canManage.value) return;
  bulkIds.value = null;
  accessTarget.value = row;
};

const seedAccess = (row) => (row.access?.inherited ? null : row.access);

const openBulkAccess = () => {
  const chosen = selectedRows.value;
  if (!chosen.length) return;
  bulkIds.value = chosen.map((r) => r.id);
  accessTarget.value = { id: null, name: chosen.length === 1 ? chosen[0].name : `${chosen.length} files`, access: seedAccess(chosen[0]) };
};

const savedIds = () => bulkIds.value ?? [accessTarget.value?.id];

const savedAccess = (row, access) => (access && row.access?.inherit_from ? { ...access, inherit_from: row.access.inherit_from } : access);

const applyAccess = (ids, access) => rows.value.forEach((r) => {
  if (!ids.includes(r.id)) return;
  r.access = savedAccess(r, access);
  if (access?.reach !== undefined) r.reach = access.reach;
});

const onAccessSaved = (access) => {
  const cleared = access === null && !bulkIds.value;
  if (!cleared) applyAccess(savedIds(), access);
  bulkIds.value = null;
  accessTarget.value = null;
  if (cleared) load();
};

const rowBook = (row) => (row.access?.inherited && row.access.book_id != null
  ? bookOfScope(row.access.book_id, row.audience_book_name, authStore.userData)
  : bookFor(row, authStore.userData));

// ---- rename ----

const openRename = (row) => {
  renaming.value = row;
  renameValue.value = row.name || '';
  renameError.value = '';
};

const saveRename = async () => {
  const name = renameValue.value.trim();
  if (!name) {
    renameError.value = 'Give the file a name.';
    return;
  }
  busy.value = true;
  try {
    const { data } = await axios.patch(`${api()}/media-items/${renaming.value.id}`, { name });
    renaming.value.name = data?.data?.name ?? name;
    notify('File renamed');
    renaming.value = null;
  } catch (error) {
    console.error(error);
    renameError.value = messageFrom(error, 'Could not rename the file.');
  } finally {
    busy.value = false;
  }
};

// ---- replace ----

const startReplace = (row) => {
  replacingId.value = row.id;
  replaceInput.value?.click();
};

const onReplaceFile = async (event) => {
  const file = event.target.files?.[0];
  event.target.value = null;
  const id = replacingId.value;
  if (!file || !id) return;

  const problem = getFileErrorMessage([file]);
  if (problem) {
    replacingId.value = null;
    notify(problem, true);
    return;
  }

  busy.value = true;
  const form = new FormData();
  form.append('file', file);
  try {
    const { data } = await axios.post(`${api()}/media-items/${id}/replace`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    // Same row, same place, same audience, same downloads — only the file.
    const row = rows.value.find((r) => r.id === id);
    if (row) Object.assign(row, data?.data ?? {});
    notify('File replaced');
  } catch (error) {
    console.error(error);
    notify(messageFrom(error, 'Could not replace the file.'), true);
  } finally {
    busy.value = false;
    replacingId.value = null;
  }
};

// ---- delete ----

const deleteCost = computed(() => {
  const chosen = deleting.value;
  const reach = chosen.reduce((sum, r) => sum + (r.reach ?? 0), 0);
  const these = chosen.length === 1 ? 'this' : 'these';
  if (!reach) return `Nobody has ${these} in their library.`;
  return `${people(reach)} will lose ${chosen.length === 1 ? 'this' : 'these files'} from their library.`;
});

const confirmDelete = (chosen) => {
  if (props.actionsDisabled || !chosen.length) return;
  deleting.value = chosen;
};

const doDelete = async () => {
  const chosen = deleting.value;
  busy.value = true;
  try {
    await axios.post(`${api()}/media-items/delete`, {
      resources: chosen.map((r) => ({ media_id: r.id, session_id: r.session_id ?? null })),
      type: 'global_resources',
    });
    const gone = new Set(chosen.map((r) => r.id));
    rows.value = rows.value.filter((r) => !gone.has(r.id));
    selected.value = selected.value.filter((id) => !gone.has(id));
    totalSizeBytes.value = Math.max(0, totalSizeBytes.value - chosen.reduce((sum, r) => sum + (r.size || 0), 0));
    notify(chosen.length === 1 ? 'File deleted' : `${chosen.length} files deleted`);
    deleting.value = [];
  } catch (error) {
    console.error(error);
    notify(messageFrom(error, 'Could not delete.'), true);
  } finally {
    busy.value = false;
  }
};

// ---- download ----

const onDownload = async (row) => {
  busy.value = true;
  try {
    // Owner and staff downloads are not logged, so they must not be shown
    // either: the +1 used to appear regardless and vanish on reload (XN-4575).
    if (await download(row)) row.download_count = (row.download_count ?? 0) + 1;
  } catch (error) {
    console.error(error);
    notify(downloadErrorMessage(error), true);
    // The row is gone or now off-limits; re-read so it stops being offered.
    if (downloadFailureIsStale(error)) await load();
  } finally {
    busy.value = false;
  }
};

// ---- row menu ----

// A broker manages the files they contributed, same as owner and hub staff;
// the rest of their board, and everyone else's list, is download only.
const menuItems = (row) => (canManage.value && mine(row)
  ? [
    { key: 'access', label: 'Who can see it' },
    { key: 'rename', label: 'Rename' },
    { key: 'replace', label: 'Replace', disabled: props.actionsDisabled || busy.value },
    { key: 'download', label: 'Download', disabled: busy.value },
    { key: 'delete', label: 'Delete', danger: true, disabled: props.actionsDisabled || busy.value },
  ]
  : [{ key: 'download', label: 'Download', disabled: busy.value }]);

const onMenu = (row, key) => {
  if (key === 'access') openAccess(row);
  else if (key === 'rename') openRename(row);
  else if (key === 'replace') startReplace(row);
  else if (key === 'download') onDownload(row);
  else if (key === 'delete') confirmDelete([row]);
};

const messageFrom = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (typeof message === 'string' && message) return message;
  if (message && typeof message === 'object') {
    const first = Object.values(message).flat()[0];
    if (first) return first;
  }
  return fallback;
};

// ---- storage ----

// No longer a readout on the page; the plan's quota is still what an upload is
// held to, so the running total and what is left of it stay.
const capacityBytes = computed(() => (capacityGb.value === null ? null : capacityGb.value * GB_CONVERSION.B));
const capacityLeftBytes = computed(() => (capacityBytes.value === null ? null : Math.max(0, capacityBytes.value - totalSizeBytes.value)));

// ---- the Overview's "Add a resource" tile ----

const focusAddResource = () => {
  const el = addEl.value?.$el ?? addEl.value;
  if (!el || el.disabled) return false;
  el.focus();
  return true;
};

defineExpose({ focusAddResource });
</script>

<template>
  <div class="files-tab">
    <div class="files-tab__toolbar">
      <XlSearchField v-model="search" placeholder="Search files" aria-label="Search files" class="files-tab__search" />
      <XlDropdown v-model="typeFilter" :options="TYPE_OPTIONS" aria-label="Filter by type" />
      <XlDropdown v-model="pageSize" :options="PAGE_SIZE_OPTIONS" aria-label="Files per page" />
      <span class="files-tab__spacer" />
      <XlButton v-if="canManage" ref="addEl" :disabled="actionsDisabled || busy" @click="uploading = true">
        <template #icon>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
        </template>
        Add a file
      </XlButton>
    </div>

    <XlEmptyState
      v-if="firstRun"
      variant="page"
      :icon="firstRun.icon"
      :headline="firstRun.headline"
      :body="firstRun.body"
      :actions="firstRun.actions.map((action) => ({ ...action, disabled: actionsDisabled }))"
      data-testid="files-first-run"
      @action="onFirstRunAction"
    />

    <XlCard v-else class="files-tab__card">
      <Loading v-if="loading || waitingOnOthers" variant="table" :rows="6" />

      <div v-else-if="!rows.length" class="files-tab__empty">
        <b>No files yet</b>
        <span>Share documents and images with every policyholder on this hub.</span>
      </div>

      <template v-else>
        <XlTable ref="tableEl">
          <template v-if="colWidths.length" #cols>
            <col v-for="(w, i) in colWidths" :key="i" :style="{ width: w }">
          </template>

          <template #head>
            <th v-if="canSelect" class="files-tab__tick" :class="{ 'is-selecting': selected.length }"><input type="checkbox" aria-label="Select every file on this page" :checked="allOnPage" @change="toggleAll"></th>

            <th v-if="canSelect && selected.length" class="files-tab__selhead" colspan="7">
              <!-- 3. the strip this replaced announced itself; a header cell that
                   appears under an unmoved focus ring has to do the same. -->
              <div class="files-tab__selbar" role="status" aria-live="polite">
                <b>{{ selected.length }} selected</b>
                <button v-if="canSelectAll" type="button" class="files-tab__selall" @click="selectAllMatching">Select all {{ filtered.length }}</button>
                <span class="files-tab__spacer" />
                <XlButton size="sm" :disabled="busy" @click="openBulkAccess">
                  <template #icon>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4.5" y="10.4" width="15" height="9.6" rx="2.5" /><path d="M8 10.4V7.6a4 4 0 0 1 8 0v2.8" /></svg>
                  </template>
                  Change who can see them
                </XlButton>
                <XlButton variant="ghost-danger" size="sm" :disabled="actionsDisabled || busy" @click="confirmDelete(selectedRows)">Delete</XlButton>
                <XlButton variant="ghost" size="sm" @click="selected = []">Clear</XlButton>
              </div>
            </th>

            <template v-else>
              <th><button type="button" class="files-tab__sort" @click="toggleSort('name')">File <i aria-hidden="true">{{ sortMark('name') }}</i></button></th>
              <th><button type="button" class="files-tab__sort" @click="toggleSort('type')">Type <i aria-hidden="true">{{ sortMark('type') }}</i></button></th>
              <th>Size</th>
              <th><button type="button" class="files-tab__sort" @click="toggleSort('added')">Added <i aria-hidden="true">{{ sortMark('added') }}</i></button></th>
              <th>Who can see it</th>
              <th>Downloads</th>
              <th class="files-tab__menu"><span class="sr-only">Actions</span></th>
            </template>
          </template>

          <tr v-for="row in paged" :key="row.id" :class="{ 'is-selected': selected.includes(row.id), 'has-menu': menuRowId === row.id }">
            <td v-if="canSelect" class="files-tab__tick"><input v-if="mine(row)" type="checkbox" :aria-label="`Select ${row.name}`" :checked="selected.includes(row.id)" @change="toggleRow(row.id)"></td>
            <td>
              <div class="files-tab__name">
                <span class="files-tab__icon"><component :is="ICONS[kindOf(row)] || IconImage" /></span>
                <span class="files-tab__name-text">
                  <b :title="row.name">{{ displayName(row.name) }}</b>
                  <small v-if="row.creator_name || row.created_by != null || row.session_title">
                    <AddedBy :created-by="row.created_by" :name="row.creator_name" :separator="Boolean(row.session_title)" /><template v-if="row.session_title">Session: {{ row.session_title }}</template>
                  </small>
                </span>
              </div>
            </td>
            <td class="files-tab__muted">{{ TYPE_LABEL[kindOf(row)] || 'File' }}</td>
            <td class="files-tab__muted files-tab__nowrap">{{ formatBytes(row.size) }}</td>
            <td class="files-tab__muted files-tab__nowrap">{{ addedText(row.created_at) }}</td>
            <td><ResourceAccessTag :access="row.access" :members="members" :manageable="canManage" :book="rowBook(row)" @manage="openAccess(row)" /></td>
            <td>{{ row.download_count ?? 0 }}</td>
            <td class="files-tab__menu">
              <ContentRowMenu :items="menuItems(row)" @select="onMenu(row, $event)" @toggle="menuRowId = $event ? row.id : null" />
            </td>
          </tr>
          <tr v-if="!paged.length">
            <td :colspan="canSelect ? 8 : 7" class="files-tab__none">Nothing matches.</td>
          </tr>
        </XlTable>

        <div v-if="lastPage > 1" class="files-tab__foot">
          <XlPagination :from="from" :to="to" :total="filtered.length" :selected="selected.length" @previous="page = Math.max(1, currentPage - 1)" @next="page = Math.min(lastPage, currentPage + 1)" />
        </div>
      </template>
    </XlCard>

    <input ref="replaceInput" type="file" :accept="ACCEPT" class="files-tab__hidden" aria-label="Replacement file" data-testid="replace-input" tabindex="-1" aria-hidden="true" @change="onReplaceFile">

    <UploadFilesModal v-if="uploading" :members="members" :capacity-left-bytes="capacityLeftBytes" @close="uploading = false" @uploaded="load" />

    <ManageAccessModal
      v-if="accessTarget"
      :resource="accessTarget"
      :book="bookFor(accessTarget, authStore.userData)"
      :inherit-book="rowBook(accessTarget)"
      :members="members"
      :bulk-ids="bulkIds"
      :inherit-from="bulkIds?.length ? null : accessTarget.access?.inherit_from"
      subject-type="media"
      @close="accessTarget = null; bulkIds = null"
      @saved="onAccessSaved"
    />

    <XlModal v-if="renaming" title="Rename file" max-width="480px" @close="renaming = null">
      <XlField v-model="renameValue" label="Name" name="name" :error="renameError" required @keydown.enter.prevent="saveRename" />
      <template #actions>
        <XlButton variant="ghost" size="sm" @click="renaming = null">Cancel</XlButton>
        <XlButton size="sm" :disabled="busy" @click="saveRename">Save</XlButton>
      </template>
    </XlModal>

    <XlModal v-if="deleting.length" :title="deleting.length === 1 ? `Delete ${deleting[0].name}?` : `Delete ${deleting.length} files?`" max-width="480px" @close="deleting = []">
      <p class="files-tab__cost">{{ deleteCost }}</p>
      <template #actions>
        <XlButton variant="ghost" size="sm" @click="deleting = []">Cancel</XlButton>
        <XlButton variant="danger" size="sm" :disabled="busy" @click="doDelete">Delete</XlButton>
      </template>
    </XlModal>
  </div>
</template>

<style scoped>
.files-tab { display: flex; flex-direction: column; gap: 14px; }

.files-tab__toolbar { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.files-tab__search { flex: 1; min-width: 200px; }
.files-tab__spacer { flex: 1; }

.files-tab__card { margin-bottom: 0; overflow: hidden; }

@media (max-width: 1199px) {
  .files-tab :deep(.xl-table__scroll) {
    background:
      linear-gradient(to right, #fff 50%, rgba(255, 255, 255, 0)),
      linear-gradient(to left, #fff 50%, rgba(255, 255, 255, 0)) 100% 0,
      linear-gradient(to right, rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0)),
      linear-gradient(to left, rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0)) 100% 0;
    background-repeat: no-repeat;
    background-size: 40px 100%, 40px 100%, 14px 100%, 14px 100%;
    background-attachment: local, local, scroll, scroll;
  }
}

.files-tab__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 48px 16px;
  text-align: center;
  color: var(--xl-color-secondary-text, #49454f);
}
.files-tab__empty b { color: var(--xl-color-primary-text, #1d1b20); font-size: var(--xl-type-body-large-size, 16px); }

.files-tab__sort { border: 0; background: transparent; font: inherit; color: inherit; padding: 0; cursor: pointer; display: inline-flex; gap: 4px; align-items: center; }
.files-tab__sort i { font-style: normal; color: var(--xl-color-secondary-text, #49454f); }

/* Both header states have to be the same height, or swapping one for the other
   moves every row below it — the bug the swap exists to avoid, in miniature.
   The buttons are 34px, so 46px holds them once the padding is off.

   Every rule here has to reach through `:deep()` to get there. XlTable styles
   its headers as `.xl-table :deep(th)`, which compiles to
   `.xl-table[data-v-…] th` and outranks a plain class in this block — so its
   `padding: 10px 16px` kept the cell 8px taller than the labels it replaces,
   and its `text-transform: uppercase` was inherited by the buttons inside,
   which is why they came out shouting. */
.files-tab :deep(.xl-table th) { height: 46px; }

.files-tab :deep(.xl-table th.files-tab__selhead),
.files-tab :deep(.xl-table th.files-tab__tick.is-selecting) {
  padding-top: 0;
  padding-bottom: 0;
  background: var(--xl-color-primary-container, #f5eff9);
}

.files-tab :deep(.xl-table th.files-tab__selhead) {
  text-transform: none;
  letter-spacing: 0;
  color: var(--xl-color-primary-text, #1d1b20);
}

.files-tab__selbar {
  display: flex;
  align-items: center;
  gap: 10px;
  /* Never wrap. A height on a cell is a minimum, not a cap, so a second line of
     buttons grows the header past 46px and moves every row below it — the exact
     failure the swap exists to prevent. The table has its own horizontal
     scroller; narrow windows scroll rather than reflow. */
  flex-wrap: nowrap;
}
.files-tab__selbar b { font-size: var(--xl-type-body-medium-size, 14px); white-space: nowrap; }

.files-tab__selall {
  border: 0;
  border-radius: var(--xl-radius-pill, 999px);
  background: color-mix(in srgb, var(--xl-color-primary, #5e2f78) 14%, white);
  color: var(--xl-color-primary, #5e2f78);
  font: inherit;
  font-size: var(--xl-type-body-small-size, 12.5px);
  font-weight: 600;
  padding: 3px 11px;
  cursor: pointer;
  white-space: nowrap;
}

.files-tab__tick { width: 38px; }
.files-tab__num { text-align: right; }
.files-tab__menu { width: 48px; text-align: right; }
.files-tab__muted { color: var(--xl-color-secondary-text, #49454f); }
.files-tab__nowrap { white-space: nowrap; }

.files-tab__name { display: flex; align-items: center; }
.files-tab__name-text { display: flex; flex-direction: column; min-width: 0; }
.files-tab__name-text small { color: var(--xl-color-secondary-text, #49454f); }

.files-tab__none { padding: 30px; text-align: center; color: var(--xl-color-secondary-text, #49454f); }

tr.is-selected td { background: color-mix(in srgb, var(--xl-color-primary, #5e2f78) 6%, white); }

.files-tab__foot { padding: 10px 12px; border-top: 1px solid var(--xl-color-outline-variant, #e7e0ec); }

.files-tab__cost { margin: 0; font-size: var(--xl-type-body-medium-size, 14px); }

.files-tab__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--xl-color-primary, #5e2f78) 8%, white);
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 11px;
}
.files-tab__icon :deep(svg) { width: 19px; height: 19px; }
/* The tile sets the row's height; the padding only has to clear it. */
.files-tab :deep(.xl-table td) { padding-top: 9px; padding-bottom: 9px; }

/* A row lights up under the cursor, so it is clear which one the menu on the
   right belongs to — and stays lit while that menu is open, because the panel
   is teleported away from the row and the pointer soon leaves it. */
.files-tab :deep(.xl-table tbody tr:hover td),
.files-tab :deep(.xl-table tbody tr.has-menu td) { background: var(--xl-color-primary-container, #f5eff9); }
.files-tab__name { gap: 12px; }

/* The 50-character cap bounds the string; this bounds the pixels, so a name
   of wide characters cannot push the columns after it off the row. */
.files-tab__name-text b {
  display: block;
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.files-tab__hidden { position: absolute; width: 1px; height: 1px; opacity: 0; overflow: hidden; pointer-events: none; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }
</style>
